import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Tooltip,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/material/styles';
import {
  Lock as LockIcon,
  LockOpen as LockOpenIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  AccountBalance as AccountBalanceIcon,
  Receipt as ReceiptIcon,
  Payment as PaymentIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Visibility as VisibilityIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { selectSelectedFacility } from '../../redux/slices/facilitiesSlice';
import {
  fetchBillingPeriods,
  fetchCurrentBillingPeriod,
  softClosePeriod,
  hardClosePeriod,
  reopenBillingPeriod,
  refreshPeriodSummaryThunk
} from '../../redux/thunks/billingThunks';
import {
  getPeriodDisplayName,
  getStatusColor,
  getStatusLabel,
  formatCurrency,
  createSnapshotForPeriod
} from '../../services/monthEndCloseService';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';

// Styled Components
const HeaderCard = styled(Card)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  marginBottom: theme.spacing(3),
  borderRadius: '12px',
  boxShadow: '0 4px 20px rgba(102, 126, 234, 0.2)',
}));

const InfoCard = styled(Card)(({ theme }) => ({
  borderRadius: '12px',
  boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
  border: `1px solid ${theme.palette.divider}`,
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.12)',
    transform: 'translateY(-2px)',
  },
}));

const StyledTable = styled(Table)(({ theme }) => ({
  '& .MuiTableCell-head': {
    backgroundColor: theme.palette.grey[50],
    fontWeight: 600,
    fontSize: '0.875rem',
    color: theme.palette.text.primary,
  },
  '& .MuiTableCell-body': {
    fontSize: '0.875rem',
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  '& .MuiTableRow-root:hover': {
    backgroundColor: theme.palette.action.hover,
  },
}));

const MonthEndClose = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const selectedFacility = useSelector(selectSelectedFacility);
  const { billingPeriods, currentPeriod, periodsLoading, periodsError } = useSelector(state => state.billing);
  
  const [selectedPeriod, setSelectedPeriod] = useState(null);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeType, setCloseType] = useState('soft'); // 'soft' or 'hard'
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [creatingSnapshotFor, setCreatingSnapshotFor] = useState(null); // { year, month }

  // Fetch billing periods when facility changes
  useEffect(() => {
    if (selectedFacility?.id) {
      dispatch(fetchBillingPeriods(selectedFacility.id));
      dispatch(fetchCurrentBillingPeriod(selectedFacility.id));
    }
  }, [dispatch, selectedFacility?.id]);

  // Get current month/year for display
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentMonth = now.getMonth() + 1;

  // Generate last 12 months for the period selector
  const getRecentMonths = useCallback(() => {
    const months = [];
    for (let i = 0; i < 12; i++) {
      const date = new Date(currentYear, currentMonth - 1 - i, 1);
      months.push({
        year: date.getFullYear(),
        month: date.getMonth() + 1,
        label: getPeriodDisplayName(date.getFullYear(), date.getMonth() + 1)
      });
    }
    return months;
  }, [currentYear, currentMonth]);

  // Find period data for a specific month
  const getPeriodData = useCallback((year, month) => {
    return billingPeriods.find(p => p.year === year && p.month === month) || {
      year,
      month,
      status: 'open',
      summary: null
    };
  }, [billingPeriods]);

  // Handle close period
  const handleClosePeriod = async () => {
    if (!selectedPeriod || !selectedFacility?.id) return;
    
    setActionLoading(true);
    setActionError(null);
    
    try {
      if (closeType === 'soft') {
        await dispatch(softClosePeriod(selectedFacility.id, selectedPeriod.year, selectedPeriod.month));
        setSuccessMessage(`${getPeriodDisplayName(selectedPeriod.year, selectedPeriod.month)} has been soft closed. Hard close will occur after the grace period.`);
      } else {
        await dispatch(hardClosePeriod(selectedFacility.id, selectedPeriod.year, selectedPeriod.month));
        setSuccessMessage(`${getPeriodDisplayName(selectedPeriod.year, selectedPeriod.month)} has been hard closed. No modifications are allowed.`);
      }
      setCloseDialogOpen(false);
      dispatch(fetchBillingPeriods(selectedFacility.id));
    } catch (error) {
      setActionError(error.message || 'Failed to close period');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle reopen period
  const handleReopenPeriod = async () => {
    if (!selectedPeriod || !selectedFacility?.id || !reopenReason.trim()) return;
    
    setActionLoading(true);
    setActionError(null);
    
    try {
      await dispatch(reopenBillingPeriod(selectedFacility.id, selectedPeriod.year, selectedPeriod.month, reopenReason));
      setSuccessMessage(`${getPeriodDisplayName(selectedPeriod.year, selectedPeriod.month)} has been reopened.`);
      setReopenDialogOpen(false);
      setReopenReason('');
      dispatch(fetchBillingPeriods(selectedFacility.id));
    } catch (error) {
      setActionError(error.message || 'Failed to reopen period');
    } finally {
      setActionLoading(false);
    }
  };

  // Handle refresh summary
  const handleRefreshSummary = async (year, month) => {
    if (!selectedFacility?.id) return;
    
    try {
      await dispatch(refreshPeriodSummaryThunk(selectedFacility.id, year, month));
      dispatch(fetchBillingPeriods(selectedFacility.id));
    } catch (error) {
      setActionError(error.message || 'Failed to refresh summary');
    }
  };

  // Handle create snapshot for hard-closed period without one
  const handleCreateSnapshot = async (year, month) => {
    if (!selectedFacility?.id) return;
    
    setCreatingSnapshotFor({ year, month });
    setActionError(null);
    
    try {
      const result = await createSnapshotForPeriod(selectedFacility.id, year, month);
      setSuccessMessage(`Snapshot created successfully for ${getPeriodDisplayName(year, month)}`);
      // Refresh periods to get the new snapshotId
      dispatch(fetchBillingPeriods(selectedFacility.id));
    } catch (error) {
      setActionError(error.response?.data?.error || error.message || 'Failed to create snapshot');
    } finally {
      setCreatingSnapshotFor(null);
    }
  };

  // Get status chip color
  const getChipColor = (status) => {
    switch (status) {
      case 'open':
        return 'success';
      case 'soft_closed':
        return 'warning';
      case 'hard_closed':
        return 'error';
      default:
        return 'default';
    }
  };

  // Get status icon
  const getStatusIcon = (status) => {
    switch (status) {
      case 'open':
        return <LockOpenIcon fontSize="small" />;
      case 'soft_closed':
        return <ScheduleIcon fontSize="small" />;
      case 'hard_closed':
        return <LockIcon fontSize="small" />;
      default:
        return <InfoIcon fontSize="small" />;
    }
  };

  if (!selectedFacility) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a facility to manage billing periods.</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <HeaderCard>
        <CardContent sx={{ py: 2, px: 3, '&:last-child': { pb: 2 } }}>
          <Grid container spacing={2} alignItems="center" justifyContent="space-between">
            <Grid item>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <IconButton
                  onClick={() => navigate('/billing')}
                  sx={{ 
                    color: 'white', 
                    backgroundColor: 'rgba(255, 255, 255, 0.15)',
                    '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
                  }}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 700, color: 'white', mb: 0.5 }}>
                    Month End Close
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                    Manage billing periods for {selectedFacility?.name}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            <Grid item>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={() => dispatch(fetchBillingPeriods(selectedFacility.id))}
                disabled={periodsLoading}
                sx={{ 
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
                }}
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </HeaderCard>

      {/* Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {(periodsError || actionError) && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setActionError(null)}>
          {periodsError || actionError}
        </Alert>
      )}

      {/* Current Period Summary */}
      {currentPeriod && (
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, borderRadius: '8px', 
                    backgroundColor: 'primary.light', color: 'primary.contrastText',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <ReceiptIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      Total Bills
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {currentPeriod.summary?.totalBills || 0}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, borderRadius: '8px', 
                    backgroundColor: 'success.light', color: 'success.contrastText',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <TrendingUpIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      Total Revenue
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'success.main' }}>
                      {formatCurrency(currentPeriod.summary?.totalRevenue)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, borderRadius: '8px', 
                    backgroundColor: 'info.light', color: 'info.contrastText',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <PaymentIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      Total Payments
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'info.main' }}>
                      {formatCurrency(currentPeriod.summary?.totalPayments)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <InfoCard>
              <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={{ 
                    p: 1, borderRadius: '8px', 
                    backgroundColor: 'warning.light', color: 'warning.contrastText',
                    display: 'flex', alignItems: 'center', justifyContent: 'center'
                  }}>
                    <AccountBalanceIcon sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography color="textSecondary" sx={{ fontSize: '0.75rem', fontWeight: 500 }}>
                      Outstanding
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'warning.main' }}>
                      {formatCurrency(currentPeriod.summary?.outstandingBalance)}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </InfoCard>
          </Grid>
        </Grid>
      )}

      {/* Status Legend */}
      <Paper sx={{ p: 2, mb: 3, backgroundColor: 'grey.50' }}>
        <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600, color: 'text.secondary' }}>
          Period Status Legend
        </Typography>
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<LockOpenIcon fontSize="small" />}
              label="Open"
              color="success"
              size="small"
            />
            <Typography variant="caption" color="text.secondary">
              Fully editable - bills can be created, modified, or deleted
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<ScheduleIcon fontSize="small" />}
              label="Soft Closed"
              color="warning"
              size="small"
              sx={{ 
                borderWidth: 2,
                borderStyle: 'dashed',
                borderColor: 'warning.main',
                backgroundColor: 'warning.light'
              }}
            />
            <Typography variant="caption" color="text.secondary">
              Warning on edits - modifications allowed but logged (auto hard-closes after grace period)
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              icon={<LockIcon fontSize="small" />}
              label="Hard Closed"
              color="error"
              size="small"
              sx={{ fontWeight: 700 }}
            />
            <Typography variant="caption" color="text.secondary">
              Fully locked - no modifications allowed, snapshot created for audit
            </Typography>
          </Box>
        </Box>
      </Paper>

      {/* Periods Table */}
      <InfoCard>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 1 }} />
            Billing Periods
          </Typography>
          
          {periodsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <StyledTable>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Bills</TableCell>
                    <TableCell align="right">Revenue</TableCell>
                    <TableCell align="right">Payments</TableCell>
                    <TableCell align="right">Outstanding</TableCell>
                    <TableCell>Closed By</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {getRecentMonths().map(({ year, month, label }) => {
                    const period = getPeriodData(year, month);
                    const isCurrentMonth = year === currentYear && month === currentMonth;
                    
                    return (
                      <TableRow key={`${year}-${month}`} sx={isCurrentMonth ? { backgroundColor: 'action.hover' } : {}}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ fontWeight: isCurrentMonth ? 600 : 400 }}>
                              {label}
                            </Typography>
                            {isCurrentMonth && (
                              <Chip size="small" label="Current" color="primary" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            icon={getStatusIcon(period.status)}
                            label={getStatusLabel(period.status)}
                            color={getChipColor(period.status)}
                            size="small"
                            sx={period.status === 'soft_closed' ? {
                              borderWidth: 2,
                              borderStyle: 'dashed',
                              borderColor: 'warning.main',
                              backgroundColor: 'warning.light'
                            } : period.status === 'hard_closed' ? {
                              fontWeight: 700
                            } : {}}
                          />
                        </TableCell>
                        <TableCell align="right">{period.summary?.totalBills || '-'}</TableCell>
                        <TableCell align="right">
                          {period.summary ? formatCurrency(period.summary.totalRevenue) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {period.summary ? formatCurrency(period.summary.totalPayments) : '-'}
                        </TableCell>
                        <TableCell align="right">
                          {period.summary ? formatCurrency(period.summary.outstandingBalance) : '-'}
                        </TableCell>
                        <TableCell>
                          {period.status === 'soft_closed' && period.softCloseDate ? (
                            <Box>
                              <Typography variant="caption" color="warning.main" sx={{ display: 'block', fontWeight: 500 }}>
                                Soft: {new Date(period.softCloseDate).toLocaleDateString()}
                              </Typography>
                              {period.hardCloseDate && (
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                  Auto-hard: {new Date(period.hardCloseDate).toLocaleDateString()}
                                </Typography>
                              )}
                            </Box>
                          ) : period.status === 'hard_closed' && period.hardCloseDate ? (
                            <Box>
                              <Typography variant="caption" color="error.main" sx={{ display: 'block', fontWeight: 500 }}>
                                Hard: {new Date(period.hardCloseDate).toLocaleDateString()}
                              </Typography>
                              {(period.closedByName || period.closedBy) && (
                                <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                                  by {period.closedByName || period.closedBy}
                                </Typography>
                              )}
                            </Box>
                          ) : period.closedAt ? (
                            <Typography variant="caption" color="textSecondary">
                              {new Date(period.closedAt).toLocaleDateString()}
                            </Typography>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                            <Tooltip title="Refresh Summary">
                              <IconButton
                                size="small"
                                onClick={() => handleRefreshSummary(year, month)}
                              >
                                <RefreshIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {period.status === 'open' && (
                              <Tooltip title="Close Period">
                                <IconButton
                                  size="small"
                                  color="warning"
                                  onClick={() => {
                                    setSelectedPeriod({ year, month });
                                    setCloseType('soft');
                                    setCloseDialogOpen(true);
                                  }}
                                >
                                  <LockIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {period.status === 'hard_closed' && period.snapshotId && (
                              <Tooltip title="View Snapshot">
                                <IconButton
                                  size="small"
                                  color="info"
                                  onClick={() => navigate(`/billing/snapshots/${period.snapshotId}`)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            {period.status === 'hard_closed' && !period.snapshotId && (
                              <Tooltip title="Create Snapshot (retroactive)">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleCreateSnapshot(year, month)}
                                  disabled={creatingSnapshotFor?.year === year && creatingSnapshotFor?.month === month}
                                >
                                  {creatingSnapshotFor?.year === year && creatingSnapshotFor?.month === month ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <AddPhotoAlternateIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </Tooltip>
                            )}
                            {period.status === 'soft_closed' && (
                              <Tooltip title="Reopen Period">
                                <IconButton
                                  size="small"
                                  color="success"
                                  onClick={() => {
                                    setSelectedPeriod({ year, month, ...period });
                                    setReopenDialogOpen(true);
                                  }}
                                >
                                  <LockOpenIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </StyledTable>
            </TableContainer>
          )}
        </CardContent>
      </InfoCard>

      {/* Close Period Dialog */}
      <Dialog open={closeDialogOpen} onClose={() => setCloseDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Close Billing Period
        </DialogTitle>
        <DialogContent>
          {selectedPeriod && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 2 }}>
                You are about to close <strong>{getPeriodDisplayName(selectedPeriod.year, selectedPeriod.month)}</strong>
              </Alert>
              
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Close Type</InputLabel>
                <Select
                  value={closeType}
                  label="Close Type"
                  onChange={(e) => setCloseType(e.target.value)}
                >
                  <MenuItem value="soft">
                    <Box>
                      <Typography>Soft Close</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Warns users but allows modifications with override. Hard close after grace period.
                      </Typography>
                    </Box>
                  </MenuItem>
                  <MenuItem value="hard">
                    <Box>
                      <Typography>Hard Close</Typography>
                      <Typography variant="caption" color="textSecondary">
                        Immediately locks the period. No modifications allowed.
                      </Typography>
                    </Box>
                  </MenuItem>
                </Select>
              </FormControl>

              {closeType === 'soft' && (
                <Alert severity="warning" icon={<ScheduleIcon />}>
                  Soft close will allow modifications with warnings. The period will automatically hard close after the configured grace period.
                </Alert>
              )}
              
              {closeType === 'hard' && (
                <Alert severity="error" icon={<LockIcon />}>
                  <strong>Warning:</strong> Hard close will permanently lock all transactions for this period and create an immutable audit snapshot. 
                  Hard-closed periods <strong>cannot be reopened</strong>. This action is irreversible.
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCloseDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={closeType === 'hard' ? 'error' : 'warning'}
            onClick={handleClosePeriod}
            disabled={actionLoading}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <LockIcon />}
          >
            {closeType === 'soft' ? 'Soft Close' : 'Hard Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reopen Period Dialog */}
      <Dialog open={reopenDialogOpen} onClose={() => setReopenDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Reopen Billing Period
        </DialogTitle>
        <DialogContent>
          {selectedPeriod && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="warning" sx={{ mb: 2 }}>
                You are about to reopen <strong>{getPeriodDisplayName(selectedPeriod.year, selectedPeriod.month)}</strong>.
                This action will be logged for audit purposes.
              </Alert>
              
              <TextField
                fullWidth
                label="Reason for Reopening"
                multiline
                rows={3}
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="Enter a detailed reason for reopening this period..."
                required
                helperText="This reason will be recorded in the audit log"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setReopenDialogOpen(false); setReopenReason(''); }}>Cancel</Button>
          <Button
            variant="contained"
            color="success"
            onClick={handleReopenPeriod}
            disabled={actionLoading || !reopenReason.trim()}
            startIcon={actionLoading ? <CircularProgress size={16} /> : <LockOpenIcon />}
          >
            Reopen Period
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MonthEndClose;

